// ABOUTME: Quote management functions backed by the Worker API.
// ABOUTME: Replaces Supabase quote queries with fetch calls.

import { api } from "@/lib/supabase/client";
import { Quote, QuotePhase } from "@/lib/types";
import { withErrorHandling } from "@/lib/errors";
import { CreateQuoteParams, BookVisitParams, SubmitPriceParams, DeclineQuoteParams } from "@/lib/types/quote-types";
import { debug } from "@/lib/utils";
import { sendQuoteRequestNotification } from "./notification";

export const requestQuote = withErrorHandling(
  async (userId: string, params: CreateQuoteParams): Promise<Quote | null> => {
    if (!params.trade_id) throw new Error('Trade ID is required');

    const { quote } = await api.post<{ quote: Quote }>('/quotes', {
      trade_id: params.trade_id, job_id: params.job_id,
    });

    try {
      if (quote) await sendQuoteRequestNotification(quote as Quote);
    } catch (e) {
      debug.error('Failed to send quote notification:', e);
    }

    return quote;
  },
  null,
  'Failed to create quote request'
);

export const bookQuoteVisit = withErrorHandling(
  async (params: BookVisitParams): Promise<Quote | null> => {
    const visitDate = new Date(params.visitAt);
    if (isNaN(visitDate.getTime())) throw new Error('Invalid date format');
    if (visitDate < new Date()) throw new Error('Visit date cannot be in the past');

    const { quote } = await api.put<{ quote: Quote }>(`/quotes/${params.quoteId}/book-visit`, {
      visit_at: params.visitAt,
    });
    return quote;
  },
  null,
  'Failed to book visit'
);

export const submitQuotePrice = withErrorHandling(
  async (params: SubmitPriceParams): Promise<Quote | null> => {
    if (!params.priceText.trim()) throw new Error('Price text cannot be empty');

    const { quote } = await api.put<{ quote: Quote }>(`/quotes/${params.quoteId}/price`, {
      price_text: params.priceText, price_photo: params.pricePhoto,
    });
    return quote;
  },
  null,
  'Failed to submit price'
);

export const declineQuote = withErrorHandling(
  async (params: DeclineQuoteParams): Promise<Quote | null> => {
    const { quote } = await api.put<{ quote: Quote }>(`/quotes/${params.quoteId}/decline`, {
      reason: params.reason,
    });
    return quote;
  },
  null,
  'Failed to decline quote'
);

export const getQuotesByUser = withErrorHandling(
  async (
    _userId: string,
    options: {
      includeRelated?: boolean; phase?: QuotePhase | QuotePhase[];
      limit?: number; offset?: number;
      orderBy?: string; orderDirection?: string;
    } = {}
  ): Promise<Quote[]> => {
    const params = new URLSearchParams();
    if (options.phase) {
      params.set('phase', Array.isArray(options.phase) ? options.phase.join(',') : options.phase);
    }
    if (options.limit) params.set('limit', String(options.limit));
    if (options.offset) params.set('offset', String(options.offset));

    const { quotes } = await api.get<{ quotes: Quote[] }>(`/quotes?${params}`);
    return quotes || [];
  },
  [],
  'Failed to load quotes'
);

export const getQuotesByJob = withErrorHandling(
  async (jobId: string, _options: { includeRelated?: boolean; phase?: QuotePhase | QuotePhase[] } = {}): Promise<Quote[]> => {
    const { quotes } = await api.get<{ quotes: Quote[] }>(`/quotes/by-job/${jobId}`);
    return quotes || [];
  },
  [],
  'Failed to load job quotes'
);

export const getQuotesByTrade = withErrorHandling(
  async (tradeId: string, options: { includeRelated?: boolean; phase?: QuotePhase | QuotePhase[]; limit?: number; offset?: number } = {}): Promise<Quote[]> => {
    const params = new URLSearchParams();
    if (options.phase) params.set('phase', Array.isArray(options.phase) ? options.phase.join(',') : options.phase);
    if (options.limit) params.set('limit', String(options.limit));
    const { quotes } = await api.get<{ quotes: Quote[] }>(`/quotes?trade_id=${tradeId}&${params}`);
    return quotes || [];
  },
  [],
  'Failed to load quotes'
);

export const getQuoteById = withErrorHandling(
  async (quoteId: string, _includeRelated = true): Promise<Quote | null> => {
    const { quote } = await api.get<{ quote: Quote }>(`/quotes/${quoteId}`);
    return quote;
  },
  null,
  'Failed to load quote'
);

export const deleteQuote = withErrorHandling(
  async (quoteId: string): Promise<boolean> => {
    if (!quoteId) throw new Error('Quote ID is required');
    await api.del(`/quotes/${quoteId}`);
    return true;
  },
  false,
  'Failed to delete quote'
);

export const deleteQuotesByJob = withErrorHandling(
  async (_jobId: string): Promise<boolean> => {
    debug.log('deleteQuotesByJob is deprecated due to cascade delete.');
    return true;
  },
  false,
  'Failed to delete job quotes'
);
